import CheckedBaggage from '@/src/assets/svgs/CheckedBaggage'
import FlightOutlined from '@/src/assets/svgs/FlightOutlined'
import {
    getRecommendedFilter,
    resetOnwardRecommendedFilter,
    resetReturnRecommendedFilter,
    setOnwardCheckedBaggageIncludedRecommendedFilter,
    setOnwardHideBudgetAirlinesRecommendedFilter,
    setReturnCheckedBaggageIncludedRecommendedFilter,
    setReturnHideBudgetAirlinesRecommendedFilter
} from '@/src/redux/slices/srpStatesSlice'
import { AppDispatch, useAppSelector } from '@/src/redux/store'
import { LocalOfferOutlined } from '@mui/icons-material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { SvgIconProps, SvgIconTypeMap } from '@mui/material/SvgIcon'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import useStopFilterManager from '../stopFilterManager'
import useSearchResults from '../useSearchResults'

export interface RecommendedFilterOption {
    id: string
    label: string
    checked: boolean
    icon:
        | ((props: SvgIconProps) => JSX.Element)
        | (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
              muiName: string
          })
    onClick: () => void
    show: boolean
    showInAppliedFilterList?: boolean
}

const useRecommendedFilterManager = (journeyLeg: 0 | 1 = 0) => {
    const t = useTranslations('common')
    const title = t('recommended')
    const dispatch = useDispatch<AppDispatch>()

    const { selectedJourneyLeg } = useSearchResults(journeyLeg)
    const { nonStopToggle, nonStopChecked, isNonstopFlightAvailable } = useStopFilterManager(selectedJourneyLeg)

    const recommendedFilter = useAppSelector(getRecommendedFilter)[selectedJourneyLeg]
    const hideBudgetAirlines = recommendedFilter.hideBudgetAirlines
    const checkedBaggageIncluded = recommendedFilter.checkedBaggageIncluded

    const checkedBaggageIncludedToggle = useCallback(() => {
        selectedJourneyLeg === 0
            ? dispatch(setOnwardCheckedBaggageIncludedRecommendedFilter(!checkedBaggageIncluded))
            : dispatch(setReturnCheckedBaggageIncludedRecommendedFilter(!checkedBaggageIncluded))
    }, [checkedBaggageIncluded, selectedJourneyLeg])

    const hideBudgetAirlinesToggle = useCallback(() => {
        selectedJourneyLeg === 0
            ? dispatch(setOnwardHideBudgetAirlinesRecommendedFilter(!hideBudgetAirlines))
            : dispatch(setReturnHideBudgetAirlinesRecommendedFilter(!hideBudgetAirlines))
    }, [hideBudgetAirlines, selectedJourneyLeg])

    const handleReset = useCallback(() => {
        selectedJourneyLeg === 0 ? dispatch(resetOnwardRecommendedFilter()) : dispatch(resetReturnRecommendedFilter())
    }, [dispatch, selectedJourneyLeg])

    const recommendedFilterOptions: RecommendedFilterOption[] = useMemo((): RecommendedFilterOption[] => {
        return [
            {
                id: 'nonstop',
                label: t('direct'),
                checked: nonStopChecked,
                icon: FlightOutlined,
                onClick: nonStopToggle,
                show: isNonstopFlightAvailable,
                showInAppliedFilterList: false
            },
            {
                id: 'checkedBaggage',
                label: t('checkedBaggageIncluded'),
                checked: checkedBaggageIncluded,
                icon: CheckedBaggage,
                onClick: checkedBaggageIncludedToggle,
                show: true,
                showInAppliedFilterList: true
            },
            {
                id: 'hideBudgetAirlines',
                label: t('hideBudgetAirlines'),
                checked: hideBudgetAirlines,
                icon: LocalOfferOutlined,
                onClick: hideBudgetAirlinesToggle,
                show: false,
                showInAppliedFilterList: false
            }
        ]
    }, [
        nonStopChecked,
        nonStopToggle,
        checkedBaggageIncludedToggle,
        hideBudgetAirlinesToggle,
        checkedBaggageIncluded,
        hideBudgetAirlines,
        isNonstopFlightAvailable
    ])

    return {
        recommendedFilterOptions,
        handleReset,
        title
    }
}

export default useRecommendedFilterManager
